import { memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as transactionsSelectors from 'store/transactionsSlice/transactionsSelectors';
import { selectUser } from 'store/authSlice/authSelectors';
import useCameraStreamWindow from 'hooks/transaction/videoWall/useCameraStreamWindow';
import useRealTimeCameraStreamBadge from 'hooks/transaction/videoWall/useRealTimeCameraStreamBadge';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { getLaneNotification, isLaneUnassigned, isVideoWallTransactionActive } from 'utils/videoWall/laneNotifications';

import CameraStream from '../../../cameras/cameraStream/CameraStream';
import ResizeHandle from './ResizeHandle';
import CameraStreamCompactHeader from '../CameraStreamCompactHeader/CameraStreamCompactHeader';
import MinimizeWindow from './MinimizeWindow';
import MaximizeWindow from './MaximizeWindow';
import CloseWindow from './CloseWindow';
import GateActions from './GateActions/GateActions';
import { compactModeHeaderBackground } from '../customStyle';

import styles from './CameraStreamWindow.module.css';

const CameraStreamWindow = () => {
    const popupRef = useRef(null);
    const closeBtnRef = useRef(null);
    const cameraStreamWindow = useSelector(transactionsSelectors.selectCameraStreamWindow);
    const { camInfo, isOpen } = cameraStreamWindow;
    const { id, relayConfig, cameraStreamComponentProps } = camInfo || {};
    const {
        camConfig,
        expandCamera,
        expandedCamId,
        fullScreenElementId,
        isFullScreenRequested,
        isTabActive,
        l1CamerasId,
        level1,
        tabAudioConfig,
        tabId,
        toggleAudio,
        transactionInfo,
    } = cameraStreamComponentProps || {};

    const hasVideoOutputId = tabId && id;
    const videoOutputId = hasVideoOutputId ? `${cameraStreamManager.getVideoElementId(tabId, id)}-window` : null;
    const streamWindow = useCameraStreamWindow({ cameraStreamWindow, videoOutputId });
    const user = useSelector(selectUser) || {};
    const activeUser = user?.username || '';
    const videoWallTransactionNotifications = useSelector(transactionsSelectors.selectVideoWallTransactionNotifications);
    const videoWallTransactionNotification = getLaneNotification(videoWallTransactionNotifications, camInfo) ?? {};
    // Live lane state, derived the same way as on the wall tile: the lane can be assigned, freed or finish while the
    // window stays open.
    const pinnedByUsername = useSelector(transactionsSelectors.selectVideoWallPinnedLaneUsername(camInfo?.gateId));
    const isTransactionUnassigned = isLaneUnassigned(videoWallTransactionNotification, pinnedByUsername);
    const isActiveTransaction = isVideoWallTransactionActive(videoWallTransactionNotification);
    const isTransactionFadingOut = !!videoWallTransactionNotification.finishedAt;
    const transactionStartTimestamp = videoWallTransactionNotification?.timestamp ?? 0;
    const assignee = videoWallTransactionNotification?.transactions?.[0]?.assignee ?? '';
    const cameraHasTransaction = Object.keys(videoWallTransactionNotification).length > 0;
    const isNewCamera = useRealTimeCameraStreamBadge(camInfo?.gateCameraId);

    useEffect(() => {
        return () => {
            if (!videoOutputId) return;
            cameraStreamManager.removeVideoOutput(videoOutputId);
        };
    }, [videoOutputId]);

    useEffect(() => {
        if (isOpen && closeBtnRef.current) {
            closeBtnRef.current.focus();
        }
    }, [videoOutputId, isOpen]);

    const { state, onMouseDown, onResizeMouseDown, handleMinimize, handleMaximize, handleClose } = streamWindow;
    const { width, height, left, top } = state;

    const windowStyle = {
        width,
        height: height + 10,
        left,
        top,
    };

    const windowHeaderStyle = {
        background: compactModeHeaderBackground({
            assignee,
            activeUser,
            hasTransaction: cameraHasTransaction,
            isTransactionUnassigned,
            isRecentlyFinished: isTransactionFadingOut,
        }).background,
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const focused = document.activeElement;
            if (focused && focused.tagName === 'BUTTON') {
                focused.click();
            }
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            const focused = document.activeElement;
            if (focused) {
                focused.blur();
            }
        }
    };

    const headerCamInfo = useMemo(() => ({ ...camInfo, gateId: camConfig?.gateId }), [camInfo, camConfig?.gateId]);

    if (!isOpen || !streamWindow) return null;

    return (
        <div
            ref={popupRef}
            className={`${styles['camera-stream-window--wrapper']}
                        ${cameraHasTransaction && assignee === activeUser ? styles['has-transaction--me-assigned'] : ''}
                        ${cameraHasTransaction && assignee !== activeUser ? styles['has-transaction--agent-assigned'] : ''}`}
            style={windowStyle}
            onKeyDown={handleKeyDown}
        >
            <div className={styles['camera-stream-window--header']} style={windowHeaderStyle} onMouseDown={onMouseDown}>
                <CameraStreamCompactHeader
                    camInfo={headerCamInfo}
                    isHover={true}
                    assignee={assignee}
                    cameraHasTransaction={cameraHasTransaction}
                    isActiveTransaction={isActiveTransaction}
                    activeUser={activeUser}
                    transactionStartTimestamp={transactionStartTimestamp}
                    hasHeaderCameraWindow
                    isTransactionUnassigned={isTransactionUnassigned}
                    isTransactionFadingOut={isTransactionFadingOut}
                    isIntercomActive={camConfig?.activeWebhook}
                    hasIntercom={camConfig?.hasIntercom}
                    isDetectionOn={camConfig?.isAIDetectionOn}
                    isNewCamera={isNewCamera}
                />
                <div className={styles['camera-stream-window-header--actions']}>
                    <MinimizeWindow minimizeHandler={handleMinimize} tabIndex={0} />
                    <MaximizeWindow maximizeHandler={handleMaximize} tabIndex={0} />
                    <CloseWindow closeHandler={handleClose} tabIndex={0} ref={closeBtnRef} />
                </div>
            </div>
            <GateActions
                transactionId={camInfo?.id}
                relayConfig={relayConfig?.length === 0 ? cameraStreamComponentProps?.camConfig?.relays : relayConfig}
            />

            <div className={styles['camera-stream-window--content']}>
                <CameraStream
                    l1CamerasId={l1CamerasId}
                    key={camConfig?.id}
                    camConfig={camConfig}
                    transactionInfo={transactionInfo}
                    tabId={tabId}
                    expandedCamId={expandedCamId}
                    expandCamera={expandCamera}
                    tabAudioConfig={tabAudioConfig}
                    level1={level1}
                    isTabActive={isTabActive}
                    toggleAudio={toggleAudio}
                    isFullScreenRequested={isFullScreenRequested}
                    fullScreenElementId={fullScreenElementId}
                    videoWallCameras={false}
                    videoWallTransactionNotification={videoWallTransactionNotification}
                    openWindowVideoIdOutput={videoOutputId}
                    forcedVideoStyles={{ width, height: height - 92, aspectRatio: '16/9' }}
                    forcedStreamType="MAIN"
                    visibleCameraIds={camInfo.cameraStreamComponentProps.visibleCameraIds}
                />
            </div>

            <ResizeHandle side="right" onMouseDown={onResizeMouseDown('right')} />
            <ResizeHandle side="left" onMouseDown={onResizeMouseDown('left')} />
            <ResizeHandle side="bottom" onMouseDown={onResizeMouseDown('bottom')} />
        </div>
    );
};

export default memo(CameraStreamWindow);
